import * as React from 'react'
import { FieldErrorMessage, FieldHelper, Input } from '../primitives'
import { useStableId } from '../hooks/useStableId'
import { FaIcon, faPlusSolid, faMinusSolid } from '@/lib/fa'

type NumberFieldProps = Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'setValue'
> & {
  setValue: (value: number | null) => void
  value?: number | null
  label?: string
  placeholder?: string
  helper?: React.ReactNode
  error?: string | null
  inputSize?: 'sm' | 'md'
  min?: number
  max?: number
  step?: number
}

export const NumberField: React.FC<NumberFieldProps> = ({
  setValue,
  label,
  value,
  error,
  disabled,
  id,
  required,
  helper,
  placeholder = '0',
  inputSize = 'md',
  min,
  max,
  step = 1,
  ...inputProps
}) => {
  const autoId = useStableId()
  const inputId = id ?? `autoid-${autoId}`
  const helperId = helper ? `${inputId}-helper` : undefined
  const errorId = error ? `${inputId}-error` : undefined

  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined

  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [inputValue, setInputValue] = React.useState('')

  React.useEffect(() => {
    setInputValue(value?.toString() ?? '')
  }, [value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    if (newValue === '' || /^-?\d*$/.test(newValue)) {
      setInputValue(newValue)
    }
  }

  const validateAndSet = (val: string) => {
    if (val === '' || val === '-') {
      setValue(null)
      return
    }

    let num = parseInt(val, 10)

    if (isNaN(num)) {
      setValue(null)
      setInputValue('')
      return
    }

    if (min !== undefined && num < min) num = min
    if (max !== undefined && num > max) num = max

    setValue(num)
    setInputValue(num.toString())
  }

  const handleInputBlur = () => {
    validateAndSet(inputValue)
  }

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return

    const currentValue = typeof value === 'number' ? value : 0
    let newValue = currentValue + step

    if (max !== undefined && newValue > max) {
      newValue = max
    }

    setValue(newValue)
  }

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return

    const currentValue = typeof value === 'number' ? value : 0
    let newValue = currentValue - step

    if (min !== undefined && newValue < min) {
      newValue = min
    }

    setValue(newValue)
  }

  return (
    <div
      ref={containerRef}
      className={`tw-relative tw-z-50 tw-inline-block ${inputSize === 'sm' ? '' : 'tw-w-full'}`}
    >
      <div
        className={`tw-relative tw-flex tw-items-center tw-gap-2 ${inputSize === 'sm' ? '' : 'tw-w-full'}`}
      >
        {label && (
          <span className="tw-text-sm tw-font-medium tw-text-gray-700">
            {label}
          </span>
        )}
        <div
          className={`tw-relative tw-flex tw-items-center ${inputSize === 'sm' ? 'tw-max-w-[90px]' : 'tw-flex-1'}`}
        >
          <button
            type="button"
            onClick={handleDecrement}
            disabled={
              disabled ||
              (min !== undefined && typeof value === 'number' && value <= min)
            }
            className={`tw-absolute tw-left-0 tw-flex tw-items-center tw-justify-center tw-text-gray-500 hover:tw-text-gray-700 disabled:tw-cursor-not-allowed disabled:tw-text-gray-300 ${inputSize === 'sm' ? 'tw-h-6 tw-w-6' : 'tw-h-8 tw-w-8'}`}
          >
            <FaIcon
              icon={faMinusSolid}
              className={`${inputSize === 'sm' ? 'tw-h-3 tw-w-3' : 'tw-h-4 tw-w-4'}`}
            />
          </button>

          <Input
            ref={inputRef}
            disabled={disabled}
            aria-describedby={describedBy}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            required={required}
            placeholder={placeholder}
            {...inputProps}
            className={`tw-block tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-text-center tw-text-sm tw-leading-tight tw-placeholder-gray-400 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 disabled:tw-text-gray-400 data-[invalid=true]:tw-border-red-500 data-[invalid=true]:focus:tw-ring-red-500 ${inputSize === 'sm' ? 'tw-px-8 tw-py-1' : 'tw-px-10 tw-py-2'}`}
          />

          <button
            type="button"
            onClick={handleIncrement}
            disabled={
              disabled ||
              (max !== undefined && typeof value === 'number' && value >= max)
            }
            className={`tw-absolute tw-right-0 tw-flex tw-items-center tw-justify-center tw-text-gray-500 hover:tw-text-gray-700 disabled:tw-cursor-not-allowed disabled:tw-text-gray-300 ${inputSize === 'sm' ? 'tw-h-6 tw-w-6' : 'tw-h-8 tw-w-8'}`}
          >
            <FaIcon
              icon={faPlusSolid}
              className={`${inputSize === 'sm' ? 'tw-h-3 tw-w-3' : 'tw-h-4 tw-w-4'}`}
            />
          </button>
        </div>
      </div>

      {helper && (
        <FieldHelper
          id={helperId}
          className="tw-mt-0.5 tw-text-xs tw-text-gray-500"
        >
          {helper}
        </FieldHelper>
      )}

      {error && (
        <FieldErrorMessage
          id={errorId}
          className="tw-mt-0.5 tw-text-xs tw-font-medium tw-text-red-600"
        >
          {error}
        </FieldErrorMessage>
      )}
    </div>
  )
}
